import React, {useEffect, useState} from 'react';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import './ControlDeExtracciones.scss';
import { InputIcon } from "primereact/inputicon";
import { IconField } from "primereact/iconfield";
import { getExtraction } from "./ControlDeExtracciones.service";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useExcelExport } from "../../../utilidades/customHooks/useExcelExport";

const Dashboard = () => {
    const { exportarAExcel } = useExcelExport();
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    const [startDate, setStartDate] = useState(firstDayOfMonth);
    const [endDate, setEndDate] = useState(lastDayOfMonth);
    const [operators, setOperators] = useState([]);
    const [selectedOperator, setSelectedOperator] = useState(null);
    const [extractionRecords, setExtractionRecords] = useState([]);
    const [monthlyExtractionData, setMonthlyExtractionData] = useState([]);
    const [topOperatorsData, setTopOperatorsData] = useState([]);
    const [metricsData, setMetricsData] = useState([]);
    const [groupByOperator, setGroupByOperator] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [first, setFirst] = useState(0);
    const [rows, setRows] = useState(10);
    const [currentPage, setCurrentPage] = useState(2);
    const [data, setData] = useState([]);

    const hiddenDescriptionsWhenGrouped = [
        "Unidades con extraccion",
        "Operadores con sensor",
        "Operadores con extracción",
        "Top 5 extracciones",
        "Porcentaje Top 5",
        "Cantidad total de trampas instaladas",
        "Sensores reportando",
    ];

    useEffect(() => {
        getExtraction(startDate, endDate, selectedOperator).then((extractionData) => {
            setOperators(extractionData?.operators);
            setExtractionRecords(extractionData.groupedData);
            setMonthlyExtractionData(extractionData.totalExtraction);
            setTopOperatorsData(extractionData.top5);
            setMetricsData(extractionData.metricsData);
            setData(transformData())
        })
    }, []);

    const executeQuery = () => {
        if(groupByOperator) {
            getExtraction(startDate, endDate, selectedOperator.code, groupByOperator).then((extractionData) => {
                setExtractionRecords(extractionData.groupedData);
                setMonthlyExtractionData(extractionData.totalExtraction);
                setTopOperatorsData(extractionData.top5);
                const filteredMetricsData = groupByOperator
                    ? extractionData.metricsData.filter(item => !hiddenDescriptionsWhenGrouped.includes(item.descripcion))
                    : extractionData.metricsData;
                setMetricsData(filteredMetricsData);
            })
        } else {
            getExtraction(startDate, endDate, null, groupByOperator).then((extractionData) => {
                setExtractionRecords(extractionData.groupedData);
                setMonthlyExtractionData(extractionData.totalExtraction);
                setTopOperatorsData(extractionData.top5);
                const filteredMetricsData = groupByOperator
                    ? extractionData.metricsData.filter(item => !hiddenDescriptionsWhenGrouped.includes(item.descripcion))
                    : extractionData.metricsData;
                setMetricsData(filteredMetricsData);
            })
        }

    };

    function getMetric(descripcion) {
        const metric = metricsData.find(item => item.descripcion === descripcion);
        return metric ? metric.valor : 'N/A';
    }

    const downloadExcel = () => {

        const dataToExport = extractionRecords.map(row => ({
            Id: row.ID,
            Unidad: row.UNIDAD,
            Trampa: row.TIENE_TRAMPA,
            Fecha: row.FECHA,
            Operador: row.OPERADOR,
            Tipo: row.TIPO,
            Litros: row.LITROS,
            Ubicacion: row.UBICACION
        }));

        exportarAExcel(dataToExport, [
            { field: 'Id', header: 'Id' },
            { field: 'Unidad', header: 'Unidad' },
            { field: 'Trampa', header: 'Trampa' },
            { field: 'Fecha', header: 'Fecha' },
            { field: 'Operador', header: 'Operador' },
            { field: 'Tipo', header: 'Tipo' },
            { field: 'Litros', header: 'Litros' },
            { field: 'Ubicacion', header: 'Ubicacion' },
        ], "REPORTE DE EXTRACCIONES");
    };

    const onPageChange = (event) => {
        setFirst(event.first);
        setRows(event.rows);
        setCurrentPage(event.page + 1);
    };

    const ubicacionBodyTemplate = (rowData) => {
        return (
            <a href={rowData.UBICACION} className="ubicacion-link">
                Ubicacion
            </a>
        );
    };

    const transformData = () => {
        if (!monthlyExtractionData || monthlyExtractionData.length === 0) return [];

        const months = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio",
            "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

        return months.map(month => {
            const item = {
                name: month,
            };

            monthlyExtractionData.forEach(yearData => {
                // Convertir de string "X.XX%" a número decimal
                const value = parseFloat(yearData[month]?.replace('%', '')) || 0;
                item[`${yearData.Año}`] = value;
            });

            return item;
        });
    };

    useEffect(() => {
        if (monthlyExtractionData && monthlyExtractionData.length > 0) {
            setData(transformData());
        }
    }, [monthlyExtractionData]);

    // Determinar años disponibles dinámicamente
    const getYears = () => {
        if (!monthlyExtractionData || monthlyExtractionData.length === 0) return [];
        return monthlyExtractionData.map(item => item.Año?.toString()).filter(Boolean);
    };

    const colors = ['#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#00C49F'];

    return (
        <div className="dashboard-container">
            <div className="dashboard-header">
                <h1>Panel de Control</h1>
            </div>

            <div className="dashboard-filters">
                <div className="filter-group">
                    <label htmlFor="startDate">Fecha de Inicio:</label>
                    <Calendar
                        id="startDate"
                        value={startDate}
                        onChange={(e) => setStartDate(e.value)}
                        showIcon
                        dateFormat="dd/mm/yy"
                    />
                </div>

                <div className="filter-group">
                    <label htmlFor="endDate">Fecha de Fin:</label>
                    <Calendar
                        id="endDate"
                        value={endDate}
                        onChange={(e) => setEndDate(e.value)}
                        showIcon
                        dateFormat="dd/mm/yy"
                    />
                </div>

                <div className="filter-group action-group">
                    <label>Ejecutar:</label>
                    <Button
                        label="Ejecutar consulta"
                        icon="pi pi-search"
                        onClick={executeQuery}
                        className="execute-button"
                    />
                </div>
            </div>

            <div className="dashboard-filters secondary-filters">
                <div className="filter-group">
                    <label htmlFor="operator">Operador:</label>
                    {operators && operators.length > 0 ? (
                        <Dropdown
                            id="operator"
                            filter
                            value={selectedOperator}
                            options={operators}
                            onChange={(e) => setSelectedOperator(e.value)}
                            optionLabel="name"
                            placeholder="Seleccione un operador"
                            className="dropdown-field"
                        />
                    ) : ''}

                </div>

                <div className="filter-group checkbox-group">
                    <label htmlFor="groupByOperator">Agrupador:</label>
                    <div className="checkbox-container">
                        <Checkbox
                            inputId="groupByOperator"
                            checked={groupByOperator}
                            onChange={(e) => setGroupByOperator(e.checked)}
                        />
                        <label htmlFor="groupByOperator" className="checkbox-label">Agrupar por operador</label>
                    </div>
                </div>
            </div>

            <div className="dashboard-metrics">
                <Card className="metric-card" title="Total Cargas">
                    <p className="metric-description">Número total de cargas registradas.</p>
                    <div className="metric-value">
                        <span className="count">{getMetric('Cargas')}</span>
                        <span className="separator">/</span>
                        <span className="amount positive">{getMetric('Litros Cargados')}</span>
                    </div>
                </Card>

                <Card className="metric-card" title="Total Extracciones">
                    <p className="metric-description">Número total de extracciones realizadas.</p>
                    <div className="metric-value">
                        <span className="count">{getMetric('Extracciones')}</span>
                        <span className="separator">/</span>
                        <span className="amount negative">{getMetric('Litros Extraídos')}</span>
                    </div>
                </Card>

                <Card className="metric-card" title="Extracciones">
                    <div className="extraction-details">
                        <div className="extraction-item">
                            <span className="label">Operadores con extracción:</span>
                            <span className="value">{getMetric('Operadores con extracción')}</span>
                        </div>
                        <div className="extraction-item">
                            <span className="label">Unidades con extracción:</span>
                            <span className="value">{getMetric('Unidades con extraccion')}</span>
                        </div>
                    </div>
                </Card>
            </div>

            <div className="dashboard-tables">
                <div className="metrics-table-container">
                    <Card>
                        <DataTable
                            value={metricsData}
                            className="metrics-table"
                            stripedRows
                            responsiveLayout="scroll"
                        >
                            <Column field="descripcion" header="Descripción" style={{width: '60%'}}/>
                            <Column field="valor" header="Valor" style={{width: '40%'}}/>
                        </DataTable>
                    </Card>
                </div>

                <div className="top-operators-container">
                    <Card title="Top 5 operadores con mayor extracción">
                        <DataTable
                            value={topOperatorsData}
                            className="top-operators-table"
                            stripedRows
                            responsiveLayout="scroll"
                        >
                            <Column field="OPERADOR" header="Operador" style={{width: '70%'}}/>
                            <Column field="LITROS" header="Litros Extraídos" style={{width: '30%'}}/>
                        </DataTable>
                    </Card>
                </div>
            </div>

            {/* Extraction Records Table */}
            <div className="extraction-records-section">
                <div className="records-header">
                    <Button
                        label="Descargar Excel"
                        icon="pi pi-file-excel"
                        className="download-excel-btn"
                        onClick={downloadExcel}
                    />
                    <IconField iconPosition="left" className="search-container">
                        <InputIcon className="pi pi-search"/>
                        <InputText
                            placeholder="Buscar:"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </IconField>

                </div>

                <DataTable
                    value={extractionRecords}
                    className="extraction-records-table"
                    responsiveLayout="scroll"
                    showGridlines
                    paginator rows={10}
                >
                    <Column field="ID" header="id" sortable/>
                    <Column field="UNIDAD" header='unidad' sortable/>
                    <Column field="TIENE_TRAMPA" header='trampa' sortable/>
                    <Column field="FECHA" header='fecha' sortable/>
                    <Column field="OPERADOR" header='chofer' sortable/>
                    <Column field="TIPO" header='tipoMovimiento' sortable/>
                    <Column field="LITROS" header='litros' sortable/>
                    <Column field="UBICACION" header='ubicacion' body={ubicacionBodyTemplate} sortable/>
                </DataTable>
            </div>

            <Card className="w-full">
                <h2 className="text-xl font-bold mb-4">Porcentaje de Extracción Total por Mes</h2>
                {data.length > 0 ? (
                    <ResponsiveContainer width="100%" height={400}>
                        <LineChart
                            data={data}
                            margin={{
                                top: 5,
                                right: 30,
                                left: 20,
                                bottom: 5,
                            }}
                        >
                            <CartesianGrid strokeDasharray="3 3"/>
                            <XAxis dataKey="name"/>
                            <YAxis domain={[0, 12]} tickFormatter={(value) => `${value}%`}/>
                            <Tooltip formatter={(value) => [`${value}%`, "Extracción"]}/>
                            <Legend/>
                            {getYears().map((year, index) => (
                                <Line
                                    key={year}
                                    type="monotone"
                                    dataKey={year}
                                    stroke={colors[index % colors.length]}
                                    activeDot={{r: 8}}
                                    name={year}
                                />
                            ))}
                        </LineChart>
                    </ResponsiveContainer>
                ) : (
                    <div className="text-center p-10">Cargando datos...</div>
                )}
            </Card>

            {/* Monthly Summary Table */}
            <div className="monthly-summary-section">
                <h2>Litros extraídos en total por mes.</h2>
                <DataTable
                    value={monthlyExtractionData}
                    className="monthly-summary-table"
                    responsiveLayout="scroll"
                    showGridlines
                >
                    <Column field="Año" header="Año"/>
                    <Column field="Enero" header="Enero"/>
                    <Column field="Febrero" header="Febrero"/>
                    <Column field="Marzo" header="Marzo"/>
                    <Column field="Abril" header="Abril"/>
                    <Column field="Mayo" header="Mayo"/>
                    <Column field="Junio" header="Junio"/>
                    <Column field="Julio" header="Julio"/>
                    <Column field="Agosto" header="Agosto"/>
                    <Column field="Septiembre" header="Septiembre"/>
                    <Column field="Octubre" header="Octubre"/>
                    <Column field="Noviembre" header="Noviembre"/>
                    <Column field="Diciembre" header="Diciembre"/>
                </DataTable>
            </div>
        </div>
    );
};

export default Dashboard;